"use client";

import { Link } from "@/components/LocaleLink";
import { useLocaleRouter } from "@/lib/hooks/useLocaleRouter";
import { useSearchParams } from "next/navigation";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { auth } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import { useLocaleStore, useTranslation } from "@/lib/hooks/useTranslation";
import { Logo } from "@/components/Logo";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { loadRecaptchaScript, executeRecaptcha } from "@/lib/recaptcha";
import { trackLogin } from "@/lib/analytics";
import { RecaptchaLegalNotice } from "@/components/RecaptchaLegalNotice";

export default function LoginPage() {
  const { tObj, locale } = useTranslation();
  const t = tObj.auth;
  const router = useLocaleRouter();
  const searchParams = useSearchParams();
  const { setAuth, _hasHydrated } = useAuthStore();
  const { setLocale } = useLocaleStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Post-login destination. Priority matches register/page.tsx:
  //  1. `?from=ats` → /resumes (pendingATSAnalysis gets picked up there)
  //  2. `?redirect_to=/safe-path` (same-origin relative only — open-redirect guard)
  //  3. /dashboard (default)
  const rawRedirect = searchParams?.get("redirect_to") ?? "";
  const safeRedirect =
    rawRedirect.startsWith("/") && !rawRedirect.startsWith("//")
      ? rawRedirect
      : null;
  const fromParam = searchParams?.get("from");
  const redirectTo =
    fromParam === "ats" ? "/resumes" : safeRedirect ?? "/dashboard";

  // Load reCAPTCHA script on mount
  useEffect(() => {
    loadRecaptchaScript();
  }, []);

  // Show loader while hydrating
  if (!_hasHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Logo size="lg" className="animate-pulse" />
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Get reCAPTCHA token
      const recaptcha_token = await executeRecaptcha("login");
      
      // Login API call
      const tokens = await auth.login({ email, password, recaptcha_token: recaptcha_token || undefined });
      
      // Get user info — guard against an HTML/empty response: calling .json()
      // on a failed fetch used to throw here and surface the generic
      // "invalid credentials" toast, which misled users whose login actually
      // succeeded but whose /users/me call failed (e.g. transient 502).
      const userRes = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"}/api/v1/users/me`,
        {
          headers: { Authorization: `Bearer ${tokens.access_token}` },
        }
      );
      if (!userRes.ok) {
        throw new Error(`users/me failed: ${userRes.status}`);
      }
      const user = await userRes.json();

      // Store auth state
      setAuth(user, tokens.access_token, tokens.refresh_token);
      
      // Set locale to user's preferred language
      if (user.ui_language) {
        setLocale(user.ui_language);
      }
      
      // Track login
      trackLogin('email');
      
      toast.success(t.loginSuccess);
      router.push(redirectTo);
    } catch (error) {
      console.error("Login error:", error);
      toast.error(t.invalidCredentials);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 px-4 py-8">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <Link href="/" className="mx-auto mb-4">
            <Logo />
          </Link>
          <CardTitle className="text-2xl">{t.login}</CardTitle>
          <CardDescription>
            {locale === "fr" ? "Entre tes identifiants pour accéder à ton compte" : "Enter your credentials to access your account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t.email}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t.password}</Label>
                <Link
                  href="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-primary"
                >
                  {t.forgotPassword}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? tObj.common.loading : t.login}
            </Button>
            <div className="mt-2 text-center">
              <RecaptchaLegalNotice />
            </div>
          </form>

          <div className="my-6 flex items-center gap-4">
            <Separator className="flex-1" />
            <span className="text-sm text-muted-foreground">{tObj.common.or}</span>
            <Separator className="flex-1" />
          </div>

          <GoogleSignInButton />
        </CardContent>
        <CardFooter className="justify-center">
          <p className="text-sm text-muted-foreground">
            {t.noAccount}{" "}
            <Link href="/register" className="font-medium text-primary hover:underline">
              {t.register}
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
